/* ─── SSE Event Types ─── */

import type {
    UberReviewCounts,
    UberReviewList,
    UberReviewListItem,
    UberReviewProjection,
    UberReviewStatusName,
    UberReviewStatusValue,
    UberReviewTargetSummary,
} from "src/components/UberReview/types";

export type SseEventType = "thought" | "message" | "build_status" | "progress" | "done" | "error" | "session_created" | "tool_call";

/* ─── Studio app Type discriminator ─── */
// Matches backend enum Spintr.Base.Enums.Studio.StudioAppType.
// CustomApp = full custom React UI + actions, iframe-rendered.
// WidgetApp = predefined chart/list/KPI widget bound to a data-fetch action, inline-rendered.
export const StudioAppType = {
    CustomApp: 1,
    WidgetApp: 2,
} as const;
export type StudioAppTypeValue = typeof StudioAppType[keyof typeof StudioAppType];

/* ─── Widget kinds (mirror of SpintrTypes.WidgetType / WidgetDisplayType) ─── */
// Only the kinds a WidgetApp can bind to. PieChart/CircleGraph (4/5) deliberately omitted
// from the picker to keep choices to common, well-supported renderers.
export const StudioWidgetKind = {
    KeyPointIndicator: 1,
    LineChart: 2,
    BarChart: 3,
    PieChart: 4,
    List: 6,
    ListOneColumn: 7,
    GaugeChart: 8,
    Leaderboard: 9,
    Table: 10,
} as const;
export type StudioWidgetKindValue = typeof StudioWidgetKind[keyof typeof StudioWidgetKind];

/* ─── Conversation Turn ─── */

export interface StudioStatusEvent {
    kind: "progress" | "build";
    label?: string;
    status?: "building" | "failed" | "success";
    attempt?: number;
    error?: string;
}

export interface StudioMessageAttachment {
    url: string;
    mediaType: string;
    fileName: string;
    width?: number;
    height?: number;
}

export interface StudioEvent {
    id: string;
    name: string;
    detail?: string | null;
    durationMs: number;
    // For buildApp: whether THIS build compiled. Set once when the bundler returns; never
    // changes afterward, even if the agent runs another buildApp later in the same turn.
    success: boolean;
}

export interface ConversationTurn {
    orderIndex: number;
    role: "user" | "assistant";
    content: string;
    thought?: string;
    createdAt?: string;
    // Matches the backend wire JSON key (StudioConversationHistoryTurnProjection.ToolCalls →
    // toolCalls). The reducer's live-streaming counterpart is `stream.streamingEvents`; we
    // didn't rename the persisted-history field because the wire format isn't ours to flip
    // for a cosmetic name.
    toolCalls?: StudioEvent[];
    attachments?: StudioMessageAttachment[];
    /** User who authored / triggered this turn (assistant turn carries the asker's id). */
    userId: number;
    userDisplayName: string;
    userAvatarUrl?: string;
}

export interface ConversationRequest {
    userMessage: string;
    appId?: number;
    modelKey?: string;
    attachments?: StudioMessageAttachment[];
    userAppBrief?: string;
    /** First-message bootstrap: locks the app's Type on the draft row. Ignored thereafter. */
    appType?: StudioAppTypeValue;
}

export interface ConversationHistoryPage {
    items: ConversationTurn[];
}

/* ─── AI Model ─── */

export interface AiModelSummary {
    key: string;
}

export interface AiModelList {
    defaultKey: string;
    models: AiModelSummary[];
}

/* ─── App File Tree ─── */

export interface AppFileTree {
    [filePath: string]: string;
}

/* ─── App Summary (list view) ─── */

export interface AppSummary {
    appId: number;
    latestVersionId: number | null;
    latestVersion: number;
    name: string;
    type: StudioAppTypeValue;
    createdAt: string;
    updatedAt: string;
    createdBy: number;
    createdByName: string | null;
    hasPublishedVersion: boolean;
    /** True when the app is live in the public app store. */
    isInStore: boolean;
    isDeleted: boolean;
    /** Status of the app's most-recent review row, or null if it has never been submitted. */
    currentReviewStatus: UberReviewStatusValue | null;
}

export interface AppListParams {
    skip?: number;
    take?: number;
    orderByColumn?: string;
    isAscending?: boolean;
    searchText?: string;
    hideDeleted?: boolean;
    /** Restrict to apps with a published version. Used by the apps list "Published" filter chip. */
    publishedOnly?: boolean;
    /** Restrict to apps without a published version. Used by the "Drafts" filter chip. */
    draftsOnly?: boolean;
    /** Restrict to apps whose most-recent review row has this status. */
    reviewStatus?: UberReviewStatusName;
    /** Filter by Studio app Type. Used by the teaser-box admin picker to show only WidgetApps. */
    type?: StudioAppTypeValue;
}

/** Aggregate counts for the /studio/apps filter chips (all in one round-trip). */
export interface StudioAppCounts {
    all: number;
    published: number;
    drafts: number;
    pending: number;
    approved: number;
    changesRequested: number;
    rejected: number;
}

export interface AppListResponse {
    items: AppSummary[];
    totalCount: number;
}

/* ─── App store (cross-instance public catalog) ─── */

export interface StoreApp {
    appId: number;
    name: string;
    description?: string | null;
    type: StudioAppTypeValue;
    iconUrl?: string | null;
    createdBy: number;
    createdByName?: string | null;
    updatedAt: string;
}

export interface StoreListResponse {
    items: StoreApp[];
    totalCount: number;
}

/** Result of installing a store app or duplicating an app: the new app the caller now owns. */
export interface StudioForkResult {
    appId: number;
    name: string;
}

/* ─── App Detail (with file tree) ─── */

export interface StudioTargetProjection {
    id: number;
    name: string;
    type: number;
    info?: string;
    image?: string;
}

export interface AppDetail {
    appId: number;
    appVersionId: number;
    version: number;
    name: string;
    description?: string | null;
    type: StudioAppTypeValue;
    fileTree: AppFileTree;
    bundledIife?: string;
    bundledCss?: string;
    widgetType?: number | null;
    widgetActionName?: string | null;
    iconUrl?: string | null;
    isPublished?: boolean;
    publishedVersion?: number | null;
    createdAt: string;
    updatedAt: string;
    createdBy: number;

    contactPerson?: StudioTargetProjection | null;
    visibleFor?: StudioTargetProjection[];
    hiddenFor?: StudioTargetProjection[];
    publishStartDate?: string;
    publishEndDate?: string | null;
    showInMenu?: boolean;
    menuItemId?: number | null;
    menuParent?: number | null;
    menuPosition?: number | null;

    // Publishing-queue surface. isStudioAdmin tells the workbench which buttons to render.
    isStudioAdmin?: boolean;
    currentReview?: UberReviewProjection | null;
}

export type StudioReviewRiskLevel = "Low" | "Medium" | "High";

export interface StudioReviewIntegration {
    key: string;
    displayName: string;
}

export interface StudioReviewInsights {
    appId: number;
    appVersionId: number;
    hasExternalIntegration: boolean;
    integrations: StudioReviewIntegration[];
    aiSummary: string | null;
    aiRecommendation: string | null;
    riskLevel: StudioReviewRiskLevel | null;
    riskRationale: string | null;
    aiUnavailable: boolean;
}

/* ─── Review flow (target-agnostic UberReview pipeline) ─── */

// Re-export the canonical UberReview types so legacy Studio call sites that import
// from "src/studio/types" keep working without a deep import.
export {
    UberReviewStatus,
} from "src/components/UberReview/types";
export type {
    UberReviewStatusValue,
    UberReviewStatusName,
    UberReviewProjection,
    UberReviewListItem,
    UberReviewList,
    UberReviewCounts,
    UberReviewTargetSummary,
} from "src/components/UberReview/types";

/* ─── App Update (PUT body — PascalCase to match server projection) ─── */

export interface StudioAppUpdateRequest {
    Name?: string;
    Description?: string;
    WidgetType?: number | null;
    WidgetActionName?: string | null;
    IconUrl?: string | null;

    ContactPersonId?: number | null;
    PublishStartDate?: string;
    PublishEndDate?: string | null;
    ClearPublishEndDate?: boolean;
    ShowInMenu?: boolean;
    MenuParent?: number | null;
    MenuPosition?: number | null;
    VisibleFor?: number[];
    HiddenFor?: number[];
}

/* ─── Current user (AI-friendly projection passed to the iframe bridge) ─── */

export interface StudioNamedId {
    id: number;
    name: string;
}

export interface StudioCurrentUser {
    id: number;
    username: string;
    name: string;
    email: string;
    imageUrl: string | null;

    isAdmin: boolean;
    isEditor: boolean;
    isExternalUser: boolean;
    isSupplier: boolean;

    isDepartmentManager: boolean;
    isOfficeManager: boolean;
    isCompanyManager: boolean;
    isUserManager: boolean;

    departmentId: number | null;
    departmentName: string | null;
    officeId: number | null;
    officeName: string | null;
    companyId: number | null;
    companyName: string | null;

    additionalDepartments: StudioNamedId[];
    groups: StudioNamedId[];
    customRoles: StudioNamedId[];
}

/* ─── Inline widget data (start-page + editor preview) ─── */

export interface StudioWidgetData {
    appId: number;
    name: string;
    displayType: number;
    iconUrl?: string | null;
    data: unknown;
    config: unknown;
    variables: Record<string, string>;
}

/* ─── App Versions ─── */

export interface AppVersionSummary {
    id: number;
    version: number;
    name: string;
    isPublished: boolean;
    createdAt: string;
    /** Status of the latest review row that targeted this specific version, or null if it was never submitted. */
    reviewStatus: UberReviewStatusValue | null;
}

export interface AppVersionListResponse {
    items: AppVersionSummary[];
    totalCount: number;
}

/* ─── Studio instance settings (global per-instance overrides) ─── */

export interface StudioSettings {
    styleInstructions: string | null;
    defaultStyleInstructions: string;
    customerContext: string | null;
    isStudioAdmin: boolean;
    isSystemAdministrator: boolean;
    studioAdminUserIds: number[];
    studioAdmins: StudioTargetProjection[];
}

export interface StudioSettingsUpdateRequest {
    StyleInstructions?: string | null;
    CustomerContext?: string | null;
    // null = leave alone; empty array = clear; otherwise replace.
    StudioAdminUserIds?: number[] | null;
}

/* ─── Per-app brief (user-authored, persisted) ─── */

export interface StudioAppBrief {
    brief: string;
}

/* ─── App Secrets (values are never returned over the wire) ─── */

export interface AppSecretSummary {
    key: string;
    createdAt: string;
    updatedAt: string;
}

/* ─── App Secret upsert (PUT body — PascalCase to match server projection) ─── */

export interface StudioAppSecretUpsertRequest {
    Value: string;
}

/* ─── Action Execution ─── */

export interface ExecuteActionRequest {
    parameters?: Record<string, unknown>;
}

export interface ExecuteActionResponse {
    result: unknown;
}

/* ─── Build Diagnostics ─── */

export interface TsDiagnostic {
    file: string | null;
    line: number | null;
    column: number | null;
    message: string | null;
    code: number | null;
    severity: string | null;
    codeLine: string | null;
}

export interface EsbuildErrorInfo {
    text: string | null;
    file: string | null;
    line: number | null;
    column: number | null;
    codeLine: string | null;
}

export interface BuildStatusPayload {
    status: string;
    error?: string;
    attempt?: number;
    diagnostics?: TsDiagnostic[];
    diagnosticCount?: number;
    esbuildErrors?: EsbuildErrorInfo[];
}

/* ─── Realtime ─── */

export interface StudioStreamingActive {
    userId: number;
    displayName: string;
    startedAt: string;
}

export interface StudioPublishedVersion {
    appId: number;
    appVersionId: number;
    version: number;
    name: string;
}

/* ─── Realtime message payloads (from RealtimeLoader.ts dispatch) ─── */

interface StudioMessageBase {
    eventName: string;
    appId: number;
    occurredAt: string;
}

/**
 * All workbench-axis events ride on this single message; `kind` discriminates.
 * Possible kinds: StreamingStarted | StreamingEnded | StreamThought | StreamMessage |
 *                 StreamProgress | StreamBuildStatus | MessageAppended | VersionPublished
 */
export interface StudioWorkbenchEventMessage extends StudioMessageBase {
    kind: string;
    messageId?: string;
    delta?: string;
    payload?: string;
    appendedMessage?: ConversationTurn;
    publishedVersion?: StudioPublishedVersion;
    initiatedByUserId?: number | null;
    initiatedByDisplayName?: string | null;
}

export interface StudioRecordUpsertedMessage extends StudioMessageBase {
    environment: string;
    collection: string;
    recordId: string;
    body?: unknown;
    bodyOmitted?: boolean;
    mutatedByUserId?: number | null;
}

export interface StudioRecordDeletedMessage extends StudioMessageBase {
    environment: string;
    collection: string;
    recordId: string;
    mutatedByUserId?: number | null;
}

export interface StudioFileUploadedMessage extends StudioMessageBase {
    environment: string;
    filePath: string;
    mutatedByUserId?: number | null;
}

export interface StudioFileDeletedMessage extends StudioMessageBase {
    environment: string;
    filePath: string;
    mutatedByUserId?: number | null;
}

/**
 * Emitted by the MCP server when its policy short-circuits a tool call (e.g. an app trying
 * to send email in debug/staging). Surfaces in the workbench Test Log so the developer can
 * see what the app would have done without anything leaving the box.
 */
export interface McpToolBlockedEventMessage {
    eventName: "Studio:McpToolBlocked";
    toolName: string;
    appId: number | null;
    arguments?: Record<string, unknown> | null;
    occurredAt: string;
}
